// src/core/dayName.ts
//
// Generates a deterministic two-word Pantone-style name for each daily puzzle,
// derived from the puzzle's color characteristics (saturation × lightness mood)
// and seeded by a hash of the actual target colors.

import type { Hsl, Puzzle } from '../types';
import { djb2, mulberry32 } from './rng';

// --- Mood classification -------------------------------------------------

type Mood = 'vivid-light' | 'vivid-mid' | 'vivid-dark'
          | 'soft-light'  | 'soft-mid'  | 'soft-dark';

function classifyMood(targets: Hsl[]): Mood {
  if (targets.length === 0) {
    throw new Error('classifyMood requires at least one target color');
  }
  let satSum = 0, lightSum = 0;
  for (const t of targets) {
    satSum += t.s;
    lightSum += t.l;
  }
  const avgSat = satSum / targets.length;
  const avgLight = lightSum / targets.length;

  const vivid = avgSat >= 62;
  const light = avgLight >= 58;
  const dark = avgLight < 42;

  if (vivid && light) return 'vivid-light';
  if (vivid && dark) return 'vivid-dark';
  if (vivid) return 'vivid-mid';
  if (light) return 'soft-light';
  if (dark) return 'soft-dark';
  return 'soft-mid';
}

// --- Curated wordlists ---------------------------------------------------
// Every word is ≤9 characters. No charged, joke, or culturally loaded terms.

const ADJECTIVE_POOLS: Record<Mood, readonly string[]> = {
  'vivid-light': ['Coral', 'Peach', 'Rose', 'Blush', 'Melon', 'Petal', 'Sorbet', 'Peony'],
  'vivid-mid':   ['Cobalt', 'Scarlet', 'Sapphire', 'Azure', 'Teal', 'Indigo', 'Crimson', 'Cerulean', 'Emerald', 'Lacquer'],
  'vivid-dark':  ['Garnet', 'Royal', 'Plum', 'Claret', 'Jewel', 'Regal', 'Wine', 'Bordeaux'],
  'soft-light':  ['Powder', 'Frost', 'Opal', 'Mist', 'Pearl', 'Ivory', 'Cloud', 'Chalk'],
  'soft-mid':    ['Slate', 'Pewter', 'Sage', 'Denim', 'Olive', 'Dove', 'Fog', 'Flint', 'Lichen', 'Suede'],
  'soft-dark':   ['Iron', 'Shadow', 'Storm', 'Ember', 'Onyx', 'Basalt', 'Anchor', 'Coal'],
};

const NOUNS: readonly string[] = [
  'Morning', 'Dusk', 'Dawn', 'Twilight', 'Horizon',
  'Solstice', 'Equinox', 'Meridian', 'Tide', 'Shore',
  'Harbor', 'Cove', 'Inlet', 'Reef', 'Lagoon',
  'Studio', 'Gallery', 'Library', 'Arcade', 'Atelier',
  'Terrace', 'Alcove', 'Silk', 'Linen', 'Velvet',
  'Canvas', 'Marble', 'Glass', 'Crystal', 'Reverie',
  'Silence', 'Prelude', 'Drift', 'Echo', 'Aria',
  'Orchard', 'Meadow', 'Garden', 'Grove', 'Valley',
  'Ridge', 'Glen', 'Basin', 'Quarter', 'Passage',
  'Sonata', 'Tableau', 'Fresco', 'Patina', 'Mosaic',
];

// --- Name generation -----------------------------------------------------

function puzzleSeed(puzzle: Puzzle): number {
  let s = 'name';
  for (const t of puzzle.targets) {
    s += `:${t.h},${t.s},${t.l}`;
  }
  return djb2(s);
}

export function dayNameFor(puzzle: Puzzle): string {
  const mood = classifyMood(puzzle.targets);
  const pool = ADJECTIVE_POOLS[mood];
  const rng = mulberry32(puzzleSeed(puzzle));
  const adj = pool[Math.floor(rng() * pool.length)];
  const noun = NOUNS[Math.floor(rng() * NOUNS.length)];
  return `${adj} ${noun}`;
}
